/**
 * AI Action Intent Classifier Service (Frontend)
 * Detects action intents and routes to appropriate action services
 * Works in conjunction with backend classifier for fast action execution
 * IMPORTANT: Supports Hospital, Community Transfer, Deceased, and Admission pages
 */

import aiReportActionsService from './ai-report-actions.service';
import aiDialogActionsService from './ai-dialog-actions.service';
import aiFilterActionsService from './ai-filter-actions.service';
import aiTransferActionsService from './ai-transfer-actions.service';
import aiCensusActionsService from './ai-census-actions.service';
import aiDateActionsService from './ai-date-actions.service';
import { HOSPITAL_CARDS_TYPE } from '../types/hospital.type';
import { CO_TRANSFER_CARDS_TYPE } from '../types/community-transfer.type';
import { DECEASED_CARDS_TYPE } from '../types/deceased.type';
import { ADMISSION_CARDS_TYPE } from '../types/admission.type';
import { OVERALL_CARDS_TYPE } from '../types/overall.type';
import { PAGE_TYPE } from '../types/pages.type';
import logger from '../utilis/logger';

class AIActionIntentClassifierService {
    constructor() {
        this.initialized = false;
        this.currentPageType = PAGE_TYPE.HOSPITAL; // Default to hospital
    }

    /**
     * Set current page type for action routing
     * @param {string} pageType - Current page type
     */
    setPageType(pageType) {
        this.currentPageType = pageType;
        logger.log('🎯 AI Action Classifier page type set to:', pageType);
    }

    /**
     * Get card type mapping based on current page
     * @returns {Object} Card type mapping
     */
    getCardTypeMapping() {
        const isCommunityPage = this.currentPageType === PAGE_TYPE.COMMUNITY_TRANSFER;
        const isDeceasedPage = this.currentPageType === PAGE_TYPE.DECEASED;
        const isAdmissionPage = this.currentPageType === PAGE_TYPE.ADMISSION;
        const isOverallPage = this.currentPageType === PAGE_TYPE.OVERALL;

        if (isOverallPage) {
            // ========== OVERALL PAGE MAPPINGS ==========
            return {
                'insurance': OVERALL_CARDS_TYPE.INSURANCE_DATA,
                'doctors': OVERALL_CARDS_TYPE.DOCTOR_DATA,
                'floors': OVERALL_CARDS_TYPE.FLOORS_DATA,
                'days': OVERALL_CARDS_TYPE.DAYS_DATA,
                'ninetyDays': OVERALL_CARDS_TYPE.NINETY_DAYS_DATA,
                'total': OVERALL_CARDS_TYPE.TOTAL,
                'totalIncoming': OVERALL_CARDS_TYPE.TOTAL_INCOMING,
                'totalOutgoing': OVERALL_CARDS_TYPE.TOTAL_OUTGOING
            };
        }

        if (isCommunityPage) {
            // ========== COMMUNITY TRANSFER PAGE MAPPINGS ==========
            return {
                'safeDischarge': CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE,
                'SNF': CO_TRANSFER_CARDS_TYPE.SNF,
                'AMA': CO_TRANSFER_CARDS_TYPE.AMA,
                'insurance': CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA,
                'returns': CO_TRANSFER_CARDS_TYPE.RETURNS_DATA,
                'ninetyDays': CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA,
                'floors': CO_TRANSFER_CARDS_TYPE.FLOORS_DATA,
                'doctors': CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA,
                'days': CO_TRANSFER_CARDS_TYPE.DAYS_DATA,
                'safeDischargeAssLiv': CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA,
                'snfFacility': CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA,
                'total': CO_TRANSFER_CARDS_TYPE.TOTAL
            };
        }

        if (isDeceasedPage) {
            // ========== DECEASED PAGE MAPPINGS ==========
            // Deceased has limited cards: Insurance, Floors, Doctors, Days, 90 Days, Total
            return {
                'insurance': DECEASED_CARDS_TYPE.INSURANCE_DATA,
                'floors': DECEASED_CARDS_TYPE.FLOORS_DATA,
                'doctors': DECEASED_CARDS_TYPE.DOCTOR_DATA,
                'days': DECEASED_CARDS_TYPE.DAYS_DATA,
                'ninetyDays': DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
                'deceased': DECEASED_CARDS_TYPE.TOTAL,
                'total': DECEASED_CARDS_TYPE.TOTAL
            };
        }

        if (isAdmissionPage) {
            // ========== ADMISSION PAGE MAPPINGS ==========
            // Admission has: Doctor, Days, Diagnosis, Insurance, Floors, Hospital
            // NO: Hospitalizations, DC/ER, Shift, Nurse, Returns (those are Hospital-specific)
            return {
                'doctor': ADMISSION_CARDS_TYPE.DOCTOR_DATA,
                'doctors': ADMISSION_CARDS_TYPE.DOCTOR_DATA,
                'days': ADMISSION_CARDS_TYPE.DAYS_DATA,
                'dx': ADMISSION_CARDS_TYPE.DX_DATA,
                'diagnosis': ADMISSION_CARDS_TYPE.DX_DATA,
                'insurance': ADMISSION_CARDS_TYPE.INSURANCE_DATA,
                'floors': ADMISSION_CARDS_TYPE.FLOORS_DATA,
                'hospital': ADMISSION_CARDS_TYPE.HOSPITAL_DATA,
                'hospitals': ADMISSION_CARDS_TYPE.HOSPITAL_DATA,
                'total': ADMISSION_CARDS_TYPE.TOTAL
            };
        }

        // ========== HOSPITAL PAGE MAPPINGS ==========
        return {
            'dcer': HOSPITAL_CARDS_TYPE.DCER_DATA,
            'hospitalizations': HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
            'insurance': HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
            'returns': HOSPITAL_CARDS_TYPE.RETURNS_DATA,
            'ninetyDays': HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
            'floors': HOSPITAL_CARDS_TYPE.FLOORS_DATA,
            'doctors': HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
            'days': HOSPITAL_CARDS_TYPE.DAYS_DATA,
            'diagnosis': HOSPITAL_CARDS_TYPE.DX_DATA,
            'shifts': HOSPITAL_CARDS_TYPE.SHIFT_DATA,
            'nurses': HOSPITAL_CARDS_TYPE.NURSE_DATA,
            'hospitals': HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
            'unplannedHospitalTransfer': HOSPITAL_CARDS_TYPE.UNPLANNED,
            'plannedHospitalTransfer': HOSPITAL_CARDS_TYPE.PLANNED,
            'total': HOSPITAL_CARDS_TYPE.TOTAL
        };
    }

    /**
     * Initialize the service
     */
    initialize() {
        this.initialized = true;
        logger.log('🎯 AI Action Intent Classifier Service initialized');
    }

    /**
     * Execute action based on classification from backend
     * @param {Object} classification - Classification result from backend
     * @param {Object} actionParams - Extracted action parameters
     * @param {Object} context - Additional context
     * @returns {Promise<Object>} Action execution result
     */
    async executeAction(classification, actionParams = {}, context = {}) {
        const { intent } = classification;

        try {
            switch (intent) {
                case 'generate_report':
                    return await this.executeReportAction(actionParams, context);

                case 'setup_alert':
                    return await this.executeAlertAction(actionParams, context);

                case 'open_dialog':
                    return await this.executeDialogAction(actionParams, context);

                case 'apply_filter':
                    return await this.executeFilterAction(actionParams, context);

                case 'change_date':
                    return await this.executeDateAction(actionParams, context);

                case 'change_census':
                    return await this.executeCensusAction(actionParams, context);

                case 'select_transfer':
                    return await this.executeTransferAction(actionParams, context);

                default:
                    return {
                        success: false,
                        error: `Unknown action intent: ${intent}`,
                        requiresFullAssistant: true
                    };
            }
        } catch (error) {
            logger.error('❌ Action execution error:', error);
            return {
                success: false,
                error: error.message || 'Action execution failed',
                requiresFullAssistant: false
            };
        }
    }

    /**
     * Execute report generation action
     */
    async executeReportAction(params, context) {
        logger.debug('📄 Executing report action - FULL PARAMS:', JSON.stringify(params, null, 2));

        try {
            const { reportType, cardType, isAutomated, frequency, parameters, customCardMetadata } = params;

            // Validate service is initialized
            if (!aiReportActionsService || typeof aiReportActionsService.generatePdfReport !== 'function') {
                throw new Error('Report actions service not initialized');
            }

            // Determine card type (default to hospitalizations if not specified)
            const targetCardType = cardType || 'hospitalizations';
            
            // CRITICAL: Extract custom card metadata from params or parameters
            const metadata = customCardMetadata || parameters?.customCardMetadata;
            const customTitle = parameters?.title || parameters?.customTitle;
            
            logger.log('🎯 Custom card metadata extracted:', {
                metadata,
                hasMetadata: !!metadata,
                fromCustomCardMetadata: !!customCardMetadata,
                fromParameters: !!parameters?.customCardMetadata,
                parameters: parameters
            });

            // Execute the appropriate report action
            if (isAutomated && frequency) {
                // CRITICAL: Map the backend card type key to the frontend constant
                const actualCardType = this.cardTypeMapping[targetCardType] || targetCardType;
                
                // CRITICAL: Map cardType to display title
                const cardTitles = {
                    'DCERData': 'DC/ER',
                    'hospitalizations': 'Hospitalizations',
                    'insuranceData': 'Insurance',
                    'returnsData': 'Returns',
                    'ninetyDaysData': '90 Days',
                    'floorsData': 'Floors',
                    'doctorData': 'Doctors',
                    'daysData': 'Days',
                    'dxData': 'Diagnosis',
                    'shiftData': 'Shifts',
                    'nurseData': 'Nurses',
                    'hospitalData': 'Hospitals',
                    'unplannedHospitalTransfer': 'Unplanned Transfer',
                    'plannedHospitalTransfer': 'Planned Transfer',
                    'total': 'Total',
                    'totalIncoming': 'Total Incoming',
                    'totalOutgoing': 'Total Outgoing'
                };
                const cardTitle = cardTitles[actualCardType] || actualCardType;
                
                logger.log('🎯 Opening automated report modal with MAPPED card type:', {
                    backendCardType: targetCardType,
                    frontendCardType: actualCardType,
                    cardTitle: cardTitle,
                    frequency,
                    reportType,
                    metadata
                });
                
                // Trigger the report dialog with pre-filled data
                // Use the mapped actualCardType which is the correct constant
                if (window.reportDialogTrigger) {
                    window.reportDialogTrigger({
                        cardType: actualCardType,  // ✅ Use the MAPPED constant (e.g., "DCERData")
                        type: frequency, // This is the interval (daily, weekly, etc.)
                        reportType: reportType,
                        reportFileType: reportType === 'excel' ? 'EXCEL' : 'PDF',
                        title: cardTitle,  // ✅ CRITICAL: Include title for ReportSummary description
                        customCardMetadata: metadata,
                        fromAI: true
                    });
                    
                    return {
                        success: true,
                        actionType: 'setup_automated_report',
                        message: `Opening automated report setup for ${targetCardType} with ${frequency} frequency`,
                        modalOpened: true
                    };
                } else {
                    logger.error('❌ window.reportDialogTrigger not available');
                    return {
                        success: false,
                        actionType: 'setup_automated_report',
                        error: 'Report modal trigger not available. Please try again.',
                        requiresFullAssistant: false
                    };
                }
            } else if (reportType === 'excel') {
                // Generate Excel report
                const result = await aiReportActionsService.generateExcelReport(
                    targetCardType, 
                    customTitle, 
                    metadata
                );
                return {
                    success: true,
                    actionType: 'generate_excel_report',
                    message: `Excel report generated successfully for ${targetCardType}`,
                    result
                };
            } else {
                // Generate PDF report (default)
                const result = await aiReportActionsService.generatePdfReport(
                    targetCardType, 
                    customTitle, 
                    metadata
                );
                return {
                    success: true,
                    actionType: 'generate_pdf_report',
                    message: `PDF report generated successfully for ${targetCardType}`,
                    result
                };
            }
        } catch (error) {
            logger.error('❌ Report action error:', error);
            return {
                success: false,
                actionType: 'generate_report',
                error: error.message || 'Failed to generate report'
            };
        }
    }

    /**
     * Execute alert setup action
     */
    async executeAlertAction(params, context) {
        logger.log('🔔 Executing alert action - FULL PARAMS:', JSON.stringify(params, null, 2));

        try {
            const { cardType, frequency, isRising, isDropping, customCardMetadata } = params;

            // CRITICAL: Map the backend card type key to the frontend constant
            const backendCardType = cardType || 'hospitalizations';
            const cardTypeMapping = this.getCardTypeMapping();
            const actualCardType = cardTypeMapping[backendCardType] || backendCardType;
            
            logger.log('🎯 Opening alert dialog with MAPPED card type:', {
                backendCardType,
                frontendCardType: actualCardType,
                frequency,
                isRising,
                isDropping,
                customCardMetadata
            });
            
            // Prepare alert data
            // CRITICAL: alertType must be an ARRAY for the form to work correctly
            const alertData = {
                cardType: actualCardType,  // ✅ Use the MAPPED constant (e.g., "DCERData")
                alertType: [frequency || 'weeks'], // ✅ ARRAY - Default to weeks if not specified
                isRising: isRising || false,
                isDropping: isDropping || false,
                customCardMetadata: customCardMetadata
            };
            
            // Trigger the alert dialog with pre-filled data
            if (window.alertDialogTrigger) {
                window.alertDialogTrigger(alertData);
                
                return {
                    success: true,
                    actionType: 'setup_alert',
                    message: `Opening alert setup for ${backendCardType}`,
                    modalOpened: true
                };
            } else {
                logger.error('❌ window.alertDialogTrigger not available');
                // Store data for later use
                window.aiPrefilledAlertData = alertData;
                return {
                    success: true,
                    actionType: 'setup_alert',
                    message: 'Alert setup data prepared. Dialog will open when ready.',
                    modalOpened: false
                };
            }
        } catch (error) {
            logger.error('❌ Alert action error:', error);
            return {
                success: false,
                actionType: 'setup_alert',
                error: error.message || 'Failed to set up alert'
            };
        }
    }

    /**
     * Execute dialog opening action
     */
    async executeDialogAction(params, context) {
        logger.log('🎭 Executing dialog action - FULL PARAMS:', JSON.stringify(params, null, 2));
        
        try {
            const { cardType, viewType, isCustomCard, customCardMetadata, parameters } = params;

            // Validate service is initialized
            if (!aiDialogActionsService || typeof aiDialogActionsService.openChartDialog !== 'function') {
                throw new Error('Dialog actions service not initialized');
            }

            // CRITICAL: Extract custom card metadata from params or parameters
            const metadata = customCardMetadata || parameters?.customCardMetadata;
            
            logger.log('🎯 Dialog action metadata extracted:', {
                metadata,
                hasMetadata: !!metadata,
                fromCustomCardMetadata: !!customCardMetadata,
                fromParameters: !!parameters?.customCardMetadata,
                viewType,
                cardType
            });

            // Determine default tab based on view type
            let defaultTab = 'Chart'; // default
            if (viewType === 'building') {
                defaultTab = 'Building';
            } else if (viewType === 'residence') {
                defaultTab = null; // null opens patient list
            } else if (viewType === 'chart') {
                defaultTab = 'Chart';
            }

            // Determine card type and map to frontend constant
            const backendCardType = cardType || 'hospitalizations';
            const cardTypeMapping = this.getCardTypeMapping();
            const targetCardType = cardTypeMapping[backendCardType] || backendCardType;

            // Build options object with custom card metadata if available
            const options = {};
            if (metadata) {
                options.customCardMetadata = metadata;
            } else if (isCustomCard) {
                options.isCustomCard = true;
            }

            logger.log('🎯 Opening dialog with:', {
                targetCardType,
                defaultTab,
                options,
                viewTypeName: viewType === 'building' ? 'building view' : 
                             viewType === 'residence' ? 'patient list' : 'chart view'
            });

            // Open the dialog
            const result = await aiDialogActionsService.openChartDialog(
                targetCardType,
                defaultTab,
                options
            );

            const viewTypeName = viewType === 'building' ? 'building view' : 
                                viewType === 'residence' ? 'patient list' : 'chart view';

            return {
                success: true,
                actionType: 'open_dialog',
                message: `Opened ${targetCardType} ${viewTypeName}`,
                result
            };
        } catch (error) {
            logger.error('❌ Dialog action error:', error);
            return {
                success: false,
                actionType: 'open_dialog',
                error: error.message || 'Failed to open dialog'
            };
        }
    }

    /**
     * Execute filter application action
     */
    async executeFilterAction(params, context) {
        logger.log('🎛️ Executing filter action:', params);

        try {
            const { cardType, itemName } = params;

            // Validate service is initialized
            if (!aiFilterActionsService || typeof aiFilterActionsService.applyFilter !== 'function') {
                throw new Error('Filter actions service not initialized');
            }

            // Apply the filter
            const result = await aiFilterActionsService.applyFilter(cardType, itemName);

            if (!result.success) {
                return result; // Return error from filter service
            }

            return {
                success: true,
                actionType: 'apply_filter',
                message: itemName 
                    ? `Filter applied: ${itemName} in ${cardType}`
                    : `Toggled all filters in ${cardType}`,
                result
            };
        } catch (error) {
            logger.error('❌ Filter action error:', error);
            return {
                success: false,
                actionType: 'apply_filter',
                error: error.message || 'Failed to apply filter'
            };
        }
    }

    /**
     * Execute date change action
     */
    async executeDateAction(params, context) {
        logger.log('📅 Executing date action:', params);

        try {
            const { preset, specificDate, year } = params;

            // Validate service is initialized
            if (!aiDateActionsService || typeof aiDateActionsService.changeDateRange !== 'function') {
                throw new Error('Date actions service not initialized');
            }

            let result;

            // Handle different date change types
            if (preset) {
                // Use preset date range
                result = await aiDateActionsService.applyDatePreset(preset);
            } else if (specificDate) {
                // Use specific date
                result = await aiDateActionsService.setSpecificDate(specificDate);
            } else if (year) {
                // Use specific year
                result = await aiDateActionsService.setYearRange(year);
            } else {
                throw new Error('No date parameters provided');
            }

            return {
                success: true,
                actionType: 'change_date',
                message: `Date range updated successfully`,
                result
            };
        } catch (error) {
            logger.error('❌ Date action error:', error);
            return {
                success: false,
                actionType: 'change_date',
                error: error.message || 'Failed to change date'
            };
        }
    }

    /**
     * Execute census change action
     */
    async executeCensusAction(params, context) {
        logger.log('📊 Executing census action:', params);

        try {
            const { percentageBy } = params;

            // Validate service is initialized
            if (!aiCensusActionsService || typeof aiCensusActionsService.changePercentageBy !== 'function') {
                throw new Error('Census actions service not initialized');
            }

            // Change percentage calculation
            const result = await aiCensusActionsService.changePercentageBy(percentageBy);

            return {
                success: true,
                actionType: 'change_census',
                message: `Percentage calculation changed to ${percentageBy}`,
                result
            };
        } catch (error) {
            logger.error('❌ Census action error:', error);
            return {
                success: false,
                actionType: 'change_census',
                error: error.message || 'Failed to change census calculation'
            };
        }
    }

    /**
     * Execute transfer selection action
     */
    async executeTransferAction(params, context) {
        logger.log('🔄 Executing transfer action:', params);

        try {
            const { transferType } = params;

            // Validate service is initialized
            if (!aiTransferActionsService || typeof aiTransferActionsService.selectTransferType !== 'function') {
                throw new Error('Transfer actions service not initialized');
            }

            // Select transfer type
            const result = await aiTransferActionsService.selectTransferType(transferType);

            return {
                success: true,
                actionType: 'select_transfer',
                message: `Transfer type changed to ${transferType}`,
                result
            };
        } catch (error) {
            logger.error('❌ Transfer action error:', error);
            return {
                success: false,
                actionType: 'select_transfer',
                error: error.message || 'Failed to select transfer type'
            };
        }
    }

    /**
     * Check if all required action services are initialized
     * @returns {Object} Initialization status
     */
    checkServiceStatus() {
        return {
            reportService: !!aiReportActionsService?.generatePdfReport,
            dialogService: !!aiDialogActionsService?.openChartDialog,
            filterService: !!aiFilterActionsService?.applyFilter,
            dateService: !!aiDateActionsService?.changeDateRange,
            censusService: !!aiCensusActionsService?.changePercentageBy,
            transferService: !!aiTransferActionsService?.selectTransferType
        };
    }

    /**
     * Get human-readable action summary
     * @param {Object} classification - Classification result
     * @param {Object} actionParams - Action parameters
     * @returns {string} Action summary
     */
    getActionSummary(classification, actionParams) {
        const { intent } = classification;

        switch (intent) {
            case 'generate_report':
                return `Generating ${actionParams.reportType || 'PDF'} report${actionParams.cardType ? ` for ${actionParams.cardType}` : ''}`;

            case 'open_dialog':
                return `Opening ${actionParams.cardType || 'chart'} ${actionParams.viewType || 'dialog'}`;

            case 'apply_filter':
                return `Applying filter${actionParams.cardType ? ` to ${actionParams.cardType}` : ''}${actionParams.itemName ? ` (${actionParams.itemName})` : ''}`;

            case 'change_date':
                if (actionParams.preset) return `Changing date to ${actionParams.preset}`;
                if (actionParams.specificDate) return `Setting date to ${actionParams.specificDate}`;
                if (actionParams.year) return `Setting year to ${actionParams.year}`;
                return 'Changing date range';

            case 'change_census':
                return `Changing percentage calculation to ${actionParams.percentageBy || 'default'}`;

            case 'select_transfer':
                return `Selecting ${actionParams.transferType || 'all'} transfers`;

            case 'general_question':
                return 'Processing your question with AI assistant';

            default:
                return 'Processing action...';
        }
    }

    /**
     * Validate that an action can be executed
     * @param {Object} classification - Classification result
     * @param {Object} actionParams - Action parameters
     * @returns {Object} Validation result
     */
    validateAction(classification, actionParams) {
        const { intent } = classification;

        // Check if service is available for this action
        const serviceStatus = this.checkServiceStatus();

        switch (intent) {
            case 'generate_report':
                if (!serviceStatus.reportService) {
                    return {
                        valid: false,
                        error: 'Report generation service not available'
                    };
                }
                break;

            case 'open_dialog':
                if (!serviceStatus.dialogService) {
                    return {
                        valid: false,
                        error: 'Dialog service not available'
                    };
                }
                break;

            case 'apply_filter':
                if (!serviceStatus.filterService) {
                    return {
                        valid: false,
                        error: 'Filter service not available'
                    };
                }
                if (!actionParams.cardType) {
                    return {
                        valid: false,
                        error: 'Card type not specified for filter action'
                    };
                }
                break;

            case 'change_date':
                if (!serviceStatus.dateService) {
                    return {
                        valid: false,
                        error: 'Date service not available'
                    };
                }
                break;

            case 'change_census':
                if (!serviceStatus.censusService) {
                    return {
                        valid: false,
                        error: 'Census service not available'
                    };
                }
                break;

            case 'select_transfer':
                if (!serviceStatus.transferService) {
                    return {
                        valid: false,
                        error: 'Transfer service not available'
                    };
                }
                break;

            default:
                // For unknown intents or general questions, no validation needed
                break;
        }

        return { valid: true };
    }
}

// Create singleton instance
const aiActionIntentClassifierService = new AIActionIntentClassifierService();

// Make available globally for debugging
if (typeof window !== 'undefined') {
    window.aiActionIntentClassifierService = aiActionIntentClassifierService;
}

export default aiActionIntentClassifierService;

